import { useState, useEffect } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Edit, Trash2, AlertCircle, X, Check, Loader2 } from "lucide-react";
import { useTranslation } from 'react-i18next';

function RawScoreResults() {
  const { api, user } = useAuth();
  const { t, i18n } = useTranslation('rawscoreresults');
  const isRTL = i18n.language === 'ar';

  // State variables
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedStream, setSelectedStream] = useState(null);
  const [students, setStudents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [scores, setScores] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentScore, setCurrentScore] = useState(null);
  const [editForm, setEditForm] = useState({
    subject_id: 0,
    raw_score: 0,
    remarks: "",
    student_id: 0,
  });

  // Fetch academic years on component mount
  useEffect(() => {
    fetchAcademicYears();
  }, []);

  // Fetch exams when year and term are selected
  useEffect(() => {
    if (selectedYear && selectedTerm) {
      fetchExams();
    }
  }, [selectedYear, selectedTerm]);

  // Fetch subjects when exam is selected
  useEffect(() => {
    if (selectedExam) {
      fetchSubjects();
      fetchClasses();
    }
  }, [selectedExam]);

  // Fetch students when stream is selected
  useEffect(() => {
    if (selectedStream) {
      fetchStudents();
    }
  }, [selectedStream, currentPage]);

  // Fetch scores when students are loaded and subject is selected
  useEffect(() => {
    if (students.length > 0 && selectedSubject) {
      fetchScores();
    }
  }, [students, selectedSubject]);

  const fetchAcademicYears = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/api/v1/academic-years/list-with-terms");
      setAcademicYears(response.data);
      // Auto-select current year if available
      const currentYear = response.data.find((year) => year.is_current_year);
      if (currentYear) {
        setSelectedYear(currentYear);
        if (currentYear.terms.length > 0) {
          setSelectedTerm(currentYear.terms[0]);
        }
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExams = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(
        `/api/v1/exams/school/${user.school_id}/all-exams`
      );
      // Filter exams by selected year and term
      const filteredExams = response.data.filter(
        (exam) =>
          exam.academic_year_id === selectedYear.academic_year_id &&
          exam.term_id === selectedTerm.term_id
      );
      setExams(filteredExams);
      setSelectedExam(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/api/v1/subjects");
      setSubjects(response.data);
      setSelectedSubject(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(
        `/api/v1/admin/schools/${user.school_reg}/classes`
      );
      // Filter classes based on selected exam's class_ids
      const filteredClasses = response.data.filter((cls) =>
        selectedExam.class_ids.includes(cls.id)
      );
      setClasses(filteredClasses);
      setSelectedClass(null);
      setSelectedStream(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(
        `/api/v1/admin/schools/${user.school_reg}/streams/${selectedStream.id}/students?page=${currentPage}&page_size=50`
      );
      if (currentPage === 1) {
        setStudents(response.data.items);
      } else {
        setStudents((prev) => [...prev, ...response.data.items]);
      }
      setTotalPages(response.data.total_pages);

      // Auto-load next page until all students are fetched
      if (currentPage < response.data.total_pages) {
        setCurrentPage((prev) => prev + 1);
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchScores = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(
        `/api/v1/exam-scores/school/${user.school_id}/all-scores`
      );
      // Filter scores based on selected exam and subject
      const filteredScores = response.data.filter(
        (score) =>
          score.exam_id === selectedExam.id &&
          score.subject_id === selectedSubject.id
      );
      setScores(filteredScores);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setSelectedTerm(year.terms.length > 0 ? year.terms[0] : null);
    resetSelections("year");
  };

  const handleTermChange = (term) => {
    setSelectedTerm(term);
    resetSelections("term");
  };

  const handleExamChange = (exam) => {
    setSelectedExam(exam);
    resetSelections("exam");
  };

  const handleSubjectChange = (subject) => {
    setSelectedSubject(subject);
    resetSelections("subject");
  };

  const handleClassChange = (cls) => {
    setSelectedClass(cls);
    setSelectedStream(null);
    resetSelections("class");
  };

  const handleStreamChange = (stream) => {
    setSelectedStream(stream);
    setStudents([]);
    setCurrentPage(1);
    resetSelections("stream");
  };

  const resetSelections = (level) => {
    const resetMap = {
      year: () => {
        setExams([]);
        setSelectedExam(null);
        setSubjects([]);
        setSelectedSubject(null);
        setClasses([]);
        setSelectedClass(null);
        setSelectedStream(null);
        setStudents([]);
        setScores([]);
      },
      term: () => {
        setExams([]);
        setSelectedExam(null);
        setSubjects([]);
        setSelectedSubject(null);
        setClasses([]);
        setSelectedClass(null);
        setSelectedStream(null);
        setStudents([]);
        setScores([]);
      },
      exam: () => {
        setSelectedSubject(null);
        setSelectedClass(null);
        setSelectedStream(null);
        setStudents([]);
        setScores([]);
      },
      subject: () => {
        setScores([]);
      },
      class: () => {
        setSelectedStream(null);
        setStudents([]);
        setScores([]);
      },
      stream: () => {
        setScores([]);
      },
    };

    if (resetMap[level]) {
      resetMap[level]();
    }
  };

  const openEditModal = (score, student) => {
    setCurrentScore(score);
    setEditForm({
      subject_id: selectedSubject.id,
      raw_score: score.raw_score,
      remarks: score.remarks || "",
      student_id: student.id,
    });
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (score, student) => {
    setCurrentScore({ ...score, student });
    setIsDeleteModalOpen(true);
  };

  const handleUpdateScore = async () => {
    try {
      setIsLoading(true);
      await api.put(
        `/api/v1/exam-scores/school/${user.school_id}/student/${editForm.student_id}/score/${currentScore.id}/update`,
        editForm
      );

      // Update scores state
      setScores((prevScores) =>
        prevScores.map((score) =>
          score.id === currentScore.id
            ? {
                ...score,
                raw_score: editForm.raw_score,
                remarks: editForm.remarks,
              }
            : score
        )
      );

      setIsEditModalOpen(false);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteScore = async () => {
    try {
      setIsLoading(true);
      await api.delete(
        `/api/v1/exam-scores/school/${user.school_id}/student/${currentScore.student.id}/score/${currentScore.id}/delete`
      );

      // Update scores state
      setScores((prevScores) =>
        prevScores.filter((score) => score.id !== currentScore.id)
      );

      setIsDeleteModalOpen(false);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const getErrorMessage = (error) => {
    if (error.response && error.response.data && error.response.data.detail) {
      return error.response.data.detail;
    }
    return error.message || t('errorOccurred');
  };

  const getStudentScore = (studentId) => {
    return scores.find((score) => score.student_id === studentId);
  };

  // Helper function to determine if a selection step is complete
  const isStepComplete = (step) => {
    switch (step) {
      case "year":
        return !!selectedYear;
      case "term":
        return !!selectedTerm;
      case "exam":
        return !!selectedExam;
      case "subject":
        return !!selectedSubject;
      case "class":
        return !!selectedClass;
      case "stream":
        return !!selectedStream;
      default:
        return false;
    }
  };

  return (
    <div className={`min-h-screen bg-gray-50 p-4 md:p-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
          {t('title')}
        </h1>

        {/* Error display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 mb-6 flex items-start">
            <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
            <div className="flex-1">
              {Array.isArray(error) ? (
                <ul className={`list-disc ${isRTL ? 'list-inside pr-4' : 'list-inside pl-4'}`}>
                  {error.map((err, idx) => (
                    <li key={idx}>{err.msg || err}</li>
                  ))}
                </ul>
              ) : (
                <p>{String(error)}</p>
              )}
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Selection steps */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
          {/* All selections in a grid layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {/* Academic Year */}
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('academicYear')}
              </label>
              <select
                className={`bg-gray-50 block w-full py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 rounded-md ${isRTL ? 'pr-3 pl-10 text-right' : 'pl-3 pr-10 text-left'}`}
                value={selectedYear?.academic_year_id || ""}
                onChange={(e) => {
                  const year = academicYears.find(
                    (y) => y.academic_year_id === Number(e.target.value)
                  );
                  if (year) handleYearChange(year);
                }}
                disabled={isLoading || academicYears.length === 0}
              >
                <option value="">{t('selectAcademicYear')}</option>
                {academicYears.map((year) => (
                  <option
                    key={year.academic_year_id}
                    value={year.academic_year_id}
                  >
                    {year.year_name} {year.is_current_year ? `(${t('current')})` : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Term */}
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('term')}
              </label>
              <select
                className={`bg-gray-50 block w-full py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 rounded-md ${isRTL ? 'pr-3 pl-10 text-right' : 'pl-3 pr-10 text-left'}`}
                value={selectedTerm?.term_id || ""}
                onChange={(e) => {
                  const term = selectedYear?.terms.find(
                    (t) => t.term_id === Number(e.target.value)
                  );
                  if (term) handleTermChange(term);
                }}
                disabled={
                  isLoading || !selectedYear || selectedYear.terms.length === 0
                }
              >
                <option value="">{t('selectTerm')}</option>
                {selectedYear?.terms.map((term) => (
                  <option key={term.term_id} value={term.term_id}>
                    {term.term_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Exam */}
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('exam')}
              </label>
              <select
                className={`bg-gray-50 block w-full py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 rounded-md ${isRTL ? 'pr-3 pl-10 text-right' : 'pl-3 pr-10 text-left'}`}
                value={selectedExam?.id || ""}
                onChange={(e) => {
                  const exam = exams.find(
                    (ex) => ex.id === Number(e.target.value)
                  );
                  if (exam) handleExamChange(exam);
                }}
                disabled={
                  isLoading || exams.length === 0 || !isStepComplete("term")
                }
              >
                <option value="">{t('selectExam')}</option>
                {exams.map((exam) => (
                  <option key={exam.id} value={exam.id}>
                    {exam.exam_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject */}
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('subject')}
              </label>
              <select
                className={`bg-gray-50 block w-full py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 rounded-md ${isRTL ? 'pr-3 pl-10 text-right' : 'pl-3 pr-10 text-left'}`}
                value={selectedSubject?.id || ""}
                onChange={(e) => {
                  const subject = subjects.find(
                    (sub) => sub.id === Number(e.target.value)
                  );
                  if (subject) handleSubjectChange(subject);
                }}
                disabled={
                  isLoading || subjects.length === 0 || !isStepComplete("exam")
                }
              >
                <option value="">{t('selectSubject')}</option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.subject_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Class */}
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('class')}
              </label>
              <select
                className={`bg-gray-50 block w-full py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 rounded-md ${isRTL ? 'pr-3 pl-10 text-right' : 'pl-3 pr-10 text-left'}`}
                value={selectedClass?.id || ""}
                onChange={(e) => {
                  const cls = classes.find(
                    (c) => c.id === Number(e.target.value)
                  );
                  if (cls) handleClassChange(cls);
                }}
                disabled={
                  isLoading ||
                  classes.length === 0 ||
                  !isStepComplete("subject")
                }
              >
                <option value="">{t('selectClass')}</option>
                {classes.map((cls) => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Stream */}
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('stream')}
              </label>
              <select
                className={`bg-gray-50 block w-full py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 rounded-md ${isRTL ? 'pr-3 pl-10 text-right' : 'pl-3 pr-10 text-left'}`}
                value={selectedStream?.id || ""}
                onChange={(e) => {
                  const stream = selectedClass?.streams.find(
                    (s) => s.id === Number(e.target.value)
                  );
                  if (stream) handleStreamChange(stream);
                }}
                disabled={
                  isLoading ||
                  !selectedClass ||
                  selectedClass.streams.length === 0 ||
                  !isStepComplete("subject")
                }
              >
                <option value="">{t('selectStream')}</option>
                {selectedClass?.streams.map((stream) => (
                  <option key={stream.id} value={stream.id}>
                    {stream.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex justify-center my-8">
            <Loader2 className="h-8 w-8 text-[#0E5252] animate-spin" />
          </div>
        )}

        {/* Students and Scores Table */}
        {isStepComplete("stream") && students.length > 0 && !isLoading && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('admissionNo')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('studentName')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('gender')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('rawScore')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('remarks')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[...students]
                  .sort((a, b) => a.admission_number - b.admission_number)
                  .map((student) => {
                    const score = getStudentScore(student.id);
                    return (
                      <tr key={student.id}>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>
                          {student.admission_number}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                          {student.name}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                          <span
                            className={`px-2 py-1 rounded-xl text-xs font-medium ${
                              student.gender.toLowerCase() === "male"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-pink-100 text-pink-800"
                            }`}
                          >
                            {t(student.gender.toLowerCase())}
                          </span>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                          {score ? score.raw_score : "-"}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                          {score ? score.remarks : "-"}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                          <div className="flex justify-center space-x-2">
                            {score ? (
                              <>
                                <button
                                  onClick={() => openEditModal(score, student)}
                                  className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                                  title={t('editScore')}
                                >
                                  <Edit className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() =>
                                    openDeleteModal(score, student)
                                  }
                                  className="p-1 text-red-600 hover:bg-red-100 rounded"
                                  title={t('deleteScore')}
                                >
                                  <Trash2 className="h-5 w-5" />
                                </button>
                              </>
                            ) : (
                              <span className="text-gray-400 text-sm">
                                {t('noScore')}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}

        {/* No students message */}
        {isStepComplete("stream") && students.length === 0 && !isLoading && (
          <div className="text-center py-8 text-gray-500">
            {t('noStudentsFound')}
          </div>
        )}

        {/* Edit Score Modal */}
        {isEditModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
              <div className="p-6">
                <h3 className={`text-lg font-medium text-gray-900 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('editStudentScore')}
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('rawScore')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0E5252] focus:border-transparent ${isRTL ? 'text-right' : 'text-left'}`}
                      value={editForm.raw_score}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          raw_score: Number(e.target.value),
                        })
                      }
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('remarks')}
                    </label>
                    <input
                      type="text"
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0E5252] focus:border-transparent ${isRTL ? 'text-right' : 'text-left'}`}
                      value={editForm.remarks}
                      onChange={(e) =>
                        setEditForm({ ...editForm, remarks: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className={`mt-6 flex justify-end space-x-2 ${isRTL ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <button
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    disabled={isLoading}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleUpdateScore}
                    className={`px-4 py-2 bg-[#0E5252] text-white rounded-md hover:bg-opacity-90 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        {t('saving')}
                      </>
                    ) : (
                      <>
                        <Check className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        {t('saveChanges')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
              <div className="p-6">
                <h3 className={`text-lg font-medium text-gray-900 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('deleteScore')}
                </h3>
                <p className={`text-gray-700 mb-6 ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('deleteConfirmation')}
                </p>

                <div className={`flex justify-end space-x-3 ${isRTL ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <button
                    onClick={() => setIsDeleteModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    disabled={isLoading}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleDeleteScore}
                    className={`px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        {t('deleting')}
                      </>
                    ) : (
                      <>
                        <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        {t('delete')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RawScoreResults;